/**
 * Data Transformation Utilities for Closing Disclosure
 *
 * Handles:
 * - PostgreSQL DECIMAL strings → JavaScript numbers
 * - Snake_case API responses → camelCase frontend types
 * - Server data → ClosingDisclosureData (5-page form structure)
 * - Version snapshots → ClosingDisclosureData
 */

import { format } from 'date-fns';
import type { CDPage1Data } from '../components/pages/CDPage1';
import type { CDPage2Data } from '../components/pages/CDPage2';
import type { CDPage3Data } from '../components/pages/CDPage3';
import type { CDPage4Data } from '../components/pages/CDPage4';
import type { CDPage5Data } from '../components/pages/CDPage5';
import type { ClosingCostLineItemData } from '../components/sections/CDClosingCostLineItem';
import type { CalculatedTotals, LineItem, Party, Transaction } from '../types';
import type { ClosingDisclosureData } from '../types/closingDisclosureData';
import { DEFAULT_CLOSING_DISCLOSURE_DATA } from '../types/closingDisclosureData';
import type { EscrowedItem, LoanPurpose, LoanTerms, LoanType } from '../types/loanTerms';
import type { CDVersion } from '../types/version';

/**
 * Server data structure passed to transformation functions
 */
export interface ServerData {
  transaction: Transaction;
  loanTerms: LoanTerms | null;
  parties: Party[];
  lineItems: LineItem[];
  calculations: CalculatedTotals | null;
}

// =============================================================================
// Numeric Parsing Utilities
// =============================================================================

/**
 * Parse a numeric value from PostgreSQL DECIMAL (stored as string) or number
 *
 * PostgreSQL JSONB stores DECIMAL fields as strings (e.g., "100.00")
 * This function safely converts them to JavaScript numbers
 */
export function parseNumeric(value: string | number | null | undefined): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Parse a boolean value from various formats
 */
export function parseBoolean(value: boolean | string | null | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1';
  }
  return Boolean(value);
}

/**
 * Parse and format a date string
 * Returns formatted date string or empty string if invalid
 */
export function parseDate(
  value: string | Date | null | undefined,
  formatStr = 'MM/dd/yyyy',
): string {
  if (!value) return '';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return format(date, formatStr);
  } catch {
    return '';
  }
}

// =============================================================================
// Snapshot Transformation Utilities
// =============================================================================

/**
 * Transform a line item from snapshot format (snake_case + string numbers) to frontend format
 */
export function transformSnapshotLineItem(li: Record<string, unknown>): LineItem {
  return {
    id: li.id as string | undefined,
    transactionId: (li.transaction_id || li.transactionId) as string,
    section: li.section as LineItem['section'],
    lineNumber: (li.line_number || li.lineNumber) as number | undefined,
    lineNumberString: (li.line_number_string || li.lineNumberString) as string | undefined,
    description: li.description as string,
    borrowerPaidAtClosing: parseNumeric(
      (li.borrower_paid_at_closing || li.borrowerPaidAtClosing) as string | number,
    ),
    borrowerPaidBeforeClosing: parseNumeric(
      (li.borrower_paid_before_closing || li.borrowerPaidBeforeClosing) as string | number,
    ),
    sellerPaidAtClosing: parseNumeric(
      (li.seller_paid_at_closing || li.sellerPaidAtClosing) as string | number,
    ),
    sellerPaidBeforeClosing: parseNumeric(
      (li.seller_paid_before_closing || li.sellerPaidBeforeClosing) as string | number,
    ),
    paidByOthers: parseNumeric((li.paid_by_others || li.paidByOthers) as string | number),
    payeeName: (li.payee_name || li.payeeName) as string | undefined,
    isOptional: parseBoolean((li.is_optional || li.isOptional) as boolean),
    isUserEntered: parseBoolean((li.is_user_entered || li.isUserEntered) as boolean),
  };
}

/**
 * Transform a party from snapshot format to frontend format
 * Note: Party uses snake_case fields as per the type definition
 */
export function transformSnapshotParty(p: Record<string, unknown>): Party {
  return {
    id: p.id as string | undefined,
    party_type: (p.party_type || p.partyType) as Party['party_type'],
    name: (p.name as string) || '',
    address_street: (p.address_street || p.addressStreet || p.address) as string | undefined,
    address_city: (p.address_city || p.addressCity) as string | undefined,
    address_state: (p.address_state || p.addressState) as string | undefined,
    address_zip_code: (p.address_zip_code || p.addressZipCode) as string | undefined,
    email: p.email as string | undefined,
    phone: p.phone as string | undefined,
    license_id: (p.license_id || p.licenseId) as string | undefined,
    nmls: p.nmls as string | undefined,
    is_primary: parseBoolean((p.is_primary || p.isPrimary) as boolean),
  };
}

/**
 * Parse a loan type from various formats to the enum type
 */
function parseLoanType(value: string | null | undefined): LoanType {
  if (!value) return 'conventional';
  const v = value.toLowerCase();
  if (v === 'fha') return 'fha';
  if (v === 'va') return 'va';
  if (v === 'usda') return 'usda';
  if (v === 'other') return 'other';
  return 'conventional';
}

/**
 * Parse a loan purpose from various formats to the enum type
 */
function parseLoanPurpose(value: string | null | undefined): LoanPurpose {
  if (!value) return 'purchase';
  const v = value.toLowerCase();
  if (v === 'refinance') return 'refinance';
  if (v === 'construction') return 'construction';
  if (v === 'other') return 'other';
  return 'purchase';
}

/**
 * Parse escrowed items from various formats
 */
function parseEscrowedItems(value: unknown): EscrowedItem[] | undefined {
  if (!value) return undefined;
  if (!Array.isArray(value)) return undefined;
  return value
    .map((item) => {
      if (typeof item !== 'string') return null;
      const v = item.toLowerCase();
      if (v === 'property_taxes') return 'property_taxes' as EscrowedItem;
      if (v === 'homeowners_insurance') return 'homeowners_insurance' as EscrowedItem;
      return 'other' as EscrowedItem;
    })
    .filter((item): item is EscrowedItem => item !== null);
}

/**
 * Transform loan terms from snapshot format to frontend format
 */
export function transformSnapshotLoanTerms(lt: Record<string, unknown>): LoanTerms {
  const monthlyPI = parseNumeric(
    (lt.monthly_principal_and_interest || lt.monthlyPrincipalAndInterest) as string | number,
  );
  const escrowPayment = parseNumeric(
    (lt.estimated_escrow_payment || lt.estimatedEscrowPayment) as string | number,
  );

  return {
    id: lt.id as string | undefined,
    transaction_id: (lt.transaction_id || lt.transactionId) as string,
    loan_amount: parseNumeric((lt.loan_amount || lt.loanAmount) as string | number),
    interest_rate: parseNumeric((lt.interest_rate || lt.interestRate) as string | number),
    loan_term_months: parseNumeric((lt.loan_term_months || lt.loanTermMonths) as string | number),
    loan_type: parseLoanType((lt.loan_type || lt.loanType) as string | undefined),
    loan_purpose: parseLoanPurpose((lt.loan_purpose || lt.loanPurpose) as string | undefined),
    loan_product: ((lt.loan_product || lt.loanProduct) as string) || 'Fixed Rate',
    loan_id: (lt.loan_id || lt.loanId) as string | undefined,
    mic: lt.mic as string | undefined,
    monthly_principal_and_interest: monthlyPI,
    estimated_escrow_payment: escrowPayment,
    estimated_taxes_insurance_assessments: parseNumeric(
      (lt.estimated_taxes_insurance_assessments || lt.estimatedTaxesInsuranceAssessments) as
        | string
        | number,
    ),
    estimated_total_monthly_payment: monthlyPI + escrowPayment,
    prepayment_penalty: parseBoolean(
      (lt.prepayment_penalty || lt.prepaymentPenalty) as boolean | string,
    ),
    balloon_payment: parseBoolean((lt.balloon_payment || lt.balloonPayment) as boolean | string),
    can_increase_after_closing: parseBoolean(
      (lt.can_increase_after_closing || lt.canIncreaseAfterClosing) as boolean | string,
    ),
    has_escrow: parseBoolean((lt.has_escrow || lt.hasEscrow) as boolean | string),
    escrowed_items: parseEscrowedItems(lt.escrowed_items || lt.escrowedItems),
    total_of_payments: parseNumeric(
      (lt.total_of_payments || lt.totalOfPayments) as string | number,
    ),
    finance_charge: parseNumeric((lt.finance_charge || lt.financeCharge) as string | number),
    annual_percentage_rate: parseNumeric(
      (lt.annual_percentage_rate || lt.annualPercentageRate) as string | number,
    ),
    total_interest_percentage: parseNumeric(
      (lt.total_interest_percentage || lt.totalInterestPercentage) as string | number,
    ),
  };
}

/**
 * Transform calculated totals from snapshot format
 */
export function transformSnapshotCalculations(
  calc: Record<string, unknown> | null | undefined,
): CalculatedTotals | null {
  if (!calc) return null;
  return {
    totalLoanCosts: parseNumeric((calc.totalLoanCosts || calc.total_loan_costs) as string | number),
    totalOtherCosts: parseNumeric(
      (calc.totalOtherCosts || calc.total_other_costs) as string | number,
    ),
    totalClosingCosts: parseNumeric(
      (calc.totalClosingCosts || calc.total_closing_costs) as string | number,
    ),
    cashToClose: parseNumeric((calc.cashToClose || calc.cash_to_close) as string | number),
    lenderCredits: parseNumeric((calc.lenderCredits || calc.lender_credits) as string | number),
    sellerCredits: parseNumeric((calc.sellerCredits || calc.seller_credits) as string | number),
  };
}

// =============================================================================
// CD Form Data Transformation
// =============================================================================

/**
 * Normalize loan type to CD form format
 */
function normalizeLoanType(type: string | undefined): 'Conventional' | 'FHA' | 'VA' | 'Other' {
  if (!type) return 'Other';
  const t = type.toLowerCase();
  if (t === 'conventional') return 'Conventional';
  if (t === 'fha') return 'FHA';
  if (t === 'va') return 'VA';
  return 'Other';
}

/**
 * Transform a line item to CD form line item data structure
 */
function transformLineItemToFormData(item: LineItem): ClosingCostLineItemData {
  return {
    lineNumber: item.lineNumberString || String(item.lineNumber || ''),
    description: item.description,
    borrowerPaid: {
      atClosing: item.borrowerPaidAtClosing || 0,
      beforeClosing: item.borrowerPaidBeforeClosing || 0,
    },
    sellerPaid: {
      atClosing: item.sellerPaidAtClosing || 0,
      beforeClosing: item.sellerPaidBeforeClosing || 0,
    },
    paidByOthers: item.paidByOthers || 0,
  };
}

/**
 * Group parties by type and extract names
 */
function getPartyNames(parties: Party[]) {
  const borrowers = parties.filter((p) => p.party_type === 'borrower');
  const sellers = parties.filter((p) => p.party_type === 'seller');
  const lenders = parties.filter((p) => p.party_type === 'lender');
  const settlementAgents = parties.filter((p) => p.party_type === 'settlement_agent');

  return {
    borrowerNames: borrowers.map((p) => p.name).join(' and '),
    sellerNames: sellers.map((p) => p.name).join(' and '),
    lenderName: lenders[0]?.name || '',
    settlementAgentName: settlementAgents[0]?.name || '',
    borrowers,
    sellers,
    lenders,
    settlementAgents,
  };
}

/**
 * Calculate closing cost totals from line items
 */
function calculateClosingCostTotals(lineItems: LineItem[]) {
  const loanCosts = lineItems.filter((item) => ['A', 'B', 'C'].includes(item.section));
  const otherCosts = lineItems.filter((item) => ['E', 'F', 'G', 'H'].includes(item.section));

  const totalLoanCosts = loanCosts.reduce(
    (sum, item) =>
      sum +
      (item.borrowerPaidAtClosing || 0) +
      (item.borrowerPaidBeforeClosing || 0) +
      (item.paidByOthers || 0),
    0,
  );

  const totalOtherCosts = otherCosts.reduce(
    (sum, item) =>
      sum +
      (item.borrowerPaidAtClosing || 0) +
      (item.borrowerPaidBeforeClosing || 0) +
      (item.paidByOthers || 0),
    0,
  );

  return {
    loanCosts,
    otherCosts,
    totalLoanCosts,
    totalOtherCosts,
    totalClosingCosts: totalLoanCosts + totalOtherCosts,
  };
}

/**
 * Transform server data to CD Page 1 format
 */
function transformToPage1Data(
  transaction: Transaction,
  loanTerms: LoanTerms | null,
  parties: Party[],
  costs: ReturnType<typeof calculateClosingCostTotals>,
  calculations: CalculatedTotals | null,
): Partial<CDPage1Data> {
  const { borrowerNames, sellerNames, lenderName, settlementAgentName } = getPartyNames(parties);

  // Settlement agent: prefer party data, fallback to transaction field
  const resolvedSettlementAgent = settlementAgentName || transaction.settlement_agent_name || '';

  return {
    header: {
      closingInformation: {
        // Date issued: use created_at as the CD issuance date
        dateIssued: parseDate(transaction.created_at),
        closingDate: parseDate(transaction.closing_date),
        disbursementDate: parseDate(transaction.disbursement_date),
        settlementAgent: resolvedSettlementAgent,
        fileNumber: transaction.file_number || '',
        propertyAddress: transaction.property_address || '',
        salePrice: parseNumeric(transaction.sale_price),
      },
      transactionInformation: {
        borrowerName: borrowerNames,
        sellerName: sellerNames,
        lenderName: lenderName,
      },
      loanInformation: loanTerms
        ? {
            loanTerm: loanTerms.loan_term_months
              ? `${Math.floor(loanTerms.loan_term_months / 12)} years`
              : '',
            purpose: loanTerms.loan_purpose === 'purchase' ? 'Purchase' : 'Refinance',
            product: loanTerms.loan_product || '',
            loanType: normalizeLoanType(loanTerms.loan_type),
            loanIdNumber: loanTerms.loan_id || transaction.file_number || '',
            mic: loanTerms.mic || '',
          }
        : {},
    },
    loanTerms: loanTerms
      ? {
          loanAmount: {
            value: parseNumeric(loanTerms.loan_amount),
            canIncrease: loanTerms.can_increase_after_closing || false,
          },
          interestRate: {
            value: parseNumeric(loanTerms.interest_rate),
            canIncrease: loanTerms.can_increase_after_closing || false,
          },
          monthlyPrincipalAndInterest: {
            value: parseNumeric(loanTerms.monthly_principal_and_interest),
            canIncrease: loanTerms.can_increase_after_closing || false,
          },
          prepaymentPenalty: {
            hasFeature: loanTerms.prepayment_penalty || false,
          },
          balloonPayment: {
            hasFeature: loanTerms.balloon_payment || false,
          },
        }
      : {},
    projectedPayments: {
      periods: [
        {
          yearRange: 'Years 1-30',
          principalAndInterest: parseNumeric(loanTerms?.monthly_principal_and_interest),
          mortgageInsurance: 0,
          estimatedEscrow: parseNumeric(loanTerms?.estimated_escrow_payment),
          estimatedTotalMonthlyPayment:
            parseNumeric(loanTerms?.monthly_principal_and_interest) +
            parseNumeric(loanTerms?.estimated_escrow_payment),
        },
      ],
      estimatedTaxesInsuranceAssessments: parseNumeric(
        loanTerms?.estimated_taxes_insurance_assessments,
      ),
      escrowDescription: "Property Taxes, Homeowner's Insurance",
      inEscrow: loanTerms?.escrowed_items?.map((e) => {
        if (e === 'property_taxes') return 'Property Taxes';
        if (e === 'homeowners_insurance') return "Homeowner's Insurance";
        return 'Other';
      }) || ['Property Taxes', "Homeowner's Insurance"],
      notInEscrow: [],
    },
    costsAtClosing: {
      closingCosts: costs.totalClosingCosts,
      loanCosts: costs.totalLoanCosts,
      otherCosts: costs.totalOtherCosts,
      lenderCredits: 0,
      cashToClose: parseNumeric(calculations?.cashToClose),
    },
  };
}

/**
 * Transform server data to CD Page 2 format
 */
function transformToPage2Data(
  costs: ReturnType<typeof calculateClosingCostTotals>,
): Partial<CDPage2Data> {
  return {
    loanCosts: {
      originationCharges: {
        items: costs.loanCosts
          .filter((item) => item.section === 'A')
          .map(transformLineItemToFormData),
      },
      servicesYouCannotShopFor: {
        items: costs.loanCosts
          .filter((item) => item.section === 'B')
          .map(transformLineItemToFormData),
      },
      servicesYouCanShopFor: {
        items: costs.loanCosts
          .filter((item) => item.section === 'C')
          .map(transformLineItemToFormData),
      },
    },
    otherCosts: {
      taxesAndGovernmentFees: {
        items: costs.otherCosts
          .filter((item) => item.section === 'E')
          .map(transformLineItemToFormData),
      },
      prepaids: {
        items: costs.otherCosts
          .filter((item) => ['F', 'G'].includes(item.section))
          .map(transformLineItemToFormData),
      },
      initialEscrowPaymentAtClosing: {
        items: [],
      },
      other: {
        items: costs.otherCosts
          .filter((item) => item.section === 'H')
          .map(transformLineItemToFormData),
      },
      totalOtherCosts: costs.totalOtherCosts,
    },
    totalClosingCosts: {
      closingCostsSubtotal: costs.totalClosingCosts,
      lenderCredits: 0,
    },
  };
}

/**
 * Transform server data to CD Page 3 format
 */
function transformToPage3Data(
  transaction: Transaction,
  loanTerms: LoanTerms | null,
  costs: ReturnType<typeof calculateClosingCostTotals>,
  calculations: CalculatedTotals | null,
): Partial<CDPage3Data> {
  const salePrice = parseNumeric(transaction.sale_price);
  const loanAmount = parseNumeric(loanTerms?.loan_amount);
  const cashToClose = parseNumeric(calculations?.cashToClose);

  return {
    calculatingCashToClose: calculations
      ? {
          totalClosingCosts: {
            label: 'Total Closing Costs (J)',
            final: costs.totalClosingCosts,
          },
          closingCostsPaidBeforeClosing: {
            label: 'Closing Costs Paid Before Closing',
            final: 0,
          },
          closingCostsFinanced: {
            label: 'Closing Costs Financed',
            final: 0,
          },
          downPaymentFromBorrower: {
            label: 'Down Payment/Funds from Borrower',
            final: 0,
          },
          deposit: {
            label: 'Deposit',
            final: 0,
          },
          fundsForBorrower: {
            label: 'Funds for Borrower',
            final: 0,
          },
          sellerCredits: {
            label: 'Seller Credits',
            final: parseNumeric(calculations?.sellerCredits),
          },
          adjustmentsAndOtherCredits: {
            label: 'Adjustments and Other Credits',
            final: 0,
          },
          cashToClose: {
            label: 'Cash to Close',
            final: cashToClose,
          },
        }
      : {},
    summariesOfTransactions: {
      borrower: {
        dueToBorrower: {
          title: 'Due from Borrower at Closing',
          items: [
            {
              lineNumber: '01',
              description: 'Sale Price of Property',
              amount: salePrice,
            },
            {
              lineNumber: '03',
              description: 'Closing Costs Paid at Closing (J)',
              amount: costs.totalClosingCosts,
            },
          ],
          adjustments: [],
          subtotal: salePrice + costs.totalClosingCosts,
        },
        paidAlreadyByBorrower: {
          title: 'Paid Already by or on Behalf of Borrower at Closing',
          items: [
            {
              lineNumber: '01',
              description: 'Deposit',
              amount: 0,
            },
            {
              lineNumber: '02',
              description: 'Loan Amount',
              amount: loanAmount,
            },
          ],
          subtotal: loanAmount,
        },
        cashToClose: cashToClose,
        cashDirection: cashToClose >= 0 ? 'from' : 'to',
      },
      seller: {
        dueToSeller: {
          title: 'Due to Seller at Closing',
          items: [
            {
              lineNumber: '01',
              description: 'Sale Price of Property',
              amount: salePrice,
            },
          ],
          adjustments: [],
          subtotal: salePrice,
        },
        dueFromSeller: {
          title: 'Due from Seller at Closing',
          items: [],
          adjustments: [],
          subtotal: 0,
        },
        cashFromSeller: 0,
        cashDirection: 'to',
      },
    },
  };
}

/**
 * Transform server data to CD Page 4 format
 */
function transformToPage4Data(
  transaction: Transaction,
  loanTerms: LoanTerms | null,
): Partial<CDPage4Data> {
  return {
    loanDisclosures: {
      assumption: {
        allowed: false,
      },
      demandFeature: {
        hasDemandFeature: false,
      },
      latePayment: {
        daysAfterDue: 15,
        feeAmount: 0,
        feeIsPercentage: true,
      },
      negativeAmortization: {
        hasNegativeAmortization: false,
      },
      partialPayments: {
        mayAccept: false,
        mayHoldInAccount: false,
        mayApplyToLoan: false,
        mayReturnToBorrower: true,
      },
      securityInterest: {
        propertyAddress: transaction.property_address || '',
      },
      escrowAccount: {
        hasEscrow: loanTerms?.has_escrow || false,
      },
    },
    escrowAccount: loanTerms?.has_escrow
      ? {
          hasEscrow: true,
          monthlyEscrowPayment: parseNumeric(loanTerms.estimated_escrow_payment),
          items:
            loanTerms.escrowed_items?.map((e) => ({
              description: e === 'property_taxes' ? 'Property Taxes' : "Homeowner's Insurance",
              monthlyAmount:
                parseNumeric(loanTerms.estimated_escrow_payment) /
                (loanTerms.escrowed_items?.length || 1),
            })) || [],
          initialEscrowPaymentAtClosing: 0,
          nonEscrowPropertyCosts: 0,
        }
      : {},
  };
}

/**
 * Transform server data to CD Page 5 format
 */
function transformToPage5Data(loanTerms: LoanTerms | null, parties: Party[]): Partial<CDPage5Data> {
  const { lenderName, settlementAgentName } = getPartyNames(parties);

  return {
    loanCalculations: loanTerms
      ? {
          totalOfPayments: parseNumeric(loanTerms.total_of_payments),
          financeCharge: parseNumeric(loanTerms.finance_charge),
          amountFinanced: parseNumeric(loanTerms.loan_amount),
          annualPercentageRate: parseNumeric(loanTerms.annual_percentage_rate),
          totalInterestPercentage: parseNumeric(loanTerms.total_interest_percentage),
        }
      : {},
    otherDisclosures: {
      appraisal: {
        rightToCopy: true,
      },
      liabilityAfterForeclosure: {
        hasDeficiencyRisk: false,
        stateAllowsDeficiency: false,
      },
      refinance: {
        refinancingSameProperty: false,
      },
    },
    contactInformation: {
      lender: {
        name: lenderName,
        address: '',
        nmls: '',
        stateLicense: '',
        contactName: '',
        contactNmls: '',
        contactStateLicense: '',
        email: '',
        phone: '',
      },
      settlementAgent: {
        name: settlementAgentName,
        address: '',
        nmls: '',
        stateLicense: '',
        contactName: '',
        contactNmls: '',
        contactStateLicense: '',
        email: '',
        phone: '',
      },
    },
    confirmReceipt: {
      applicants: [
        { name: '', signature: '', date: '' },
        { name: '', signature: '', date: '' },
      ],
    },
  };
}

// =============================================================================
// Main Transformation Functions
// =============================================================================

/**
 * Transform server data to ClosingDisclosureData format
 *
 * This is the main transformation function that takes raw API data
 * and converts it to the 5-page form structure.
 */
export function transformServerDataToCDForm(data: ServerData): ClosingDisclosureData {
  const { transaction, loanTerms, parties, lineItems, calculations } = data;

  if (!transaction) {
    return DEFAULT_CLOSING_DISCLOSURE_DATA;
  }

  const costs = calculateClosingCostTotals(lineItems);

  return {
    loanId: transaction.id || '',
    formId: 'CFPB Form H-25(A)',
    totalPages: 5,
    page1: transformToPage1Data(transaction, loanTerms, parties, costs, calculations),
    page2: transformToPage2Data(costs),
    page3: transformToPage3Data(transaction, loanTerms, costs, calculations),
    page4: transformToPage4Data(transaction, loanTerms),
    page5: transformToPage5Data(loanTerms, parties),
  };
}

/**
 * Transform a version snapshot to ClosingDisclosureData format
 *
 * Used in Preview mode when we have a saved version with snapshot data.
 * Handles snake_case + string numbers from PostgreSQL JSONB.
 */
export function transformSnapshotToCDForm(version: CDVersion): ClosingDisclosureData {
  const snapshot = version.snapshotData;

  if (!snapshot) {
    return DEFAULT_CLOSING_DISCLOSURE_DATA;
  }

  // Transform snapshot data to normalized format
  const parties = (snapshot.parties || []).map((p) =>
    transformSnapshotParty(p as Record<string, unknown>),
  );
  const lineItems = (snapshot.lineItems || []).map((li) =>
    transformSnapshotLineItem(li as Record<string, unknown>),
  );
  const loanTerms = snapshot.loanTerms
    ? transformSnapshotLoanTerms(snapshot.loanTerms as Record<string, unknown>)
    : null;
  const calculations = transformSnapshotCalculations(
    snapshot.draft?.calculated_totals as Record<string, unknown> | null,
  );

  // Get transaction from snapshot or create minimal one
  const formData = snapshot.draft?.form_data as Record<string, unknown> | undefined;
  const transaction: Transaction = {
    id: version.transactionId || '',
    property_address: (formData?.property_address as string) || '',
    sale_price: parseNumeric(formData?.sale_price as string | number),
    closing_date: (formData?.closing_date as string) || '',
    file_number: (formData?.file_number as string) || '',
    status: (snapshot.draft?.status as Transaction['status']) || 'draft',
    created_at: version.createdAt || new Date().toISOString(),
    updated_at: version.createdAt || new Date().toISOString(),
  };

  const costs = calculateClosingCostTotals(lineItems);

  return {
    loanId: transaction.id,
    formId: 'CFPB Form H-25(A)',
    totalPages: 5,
    page1: transformToPage1Data(transaction, loanTerms, parties, costs, calculations),
    page2: transformToPage2Data(costs),
    page3: transformToPage3Data(transaction, loanTerms, costs, calculations),
    page4: transformToPage4Data(transaction, loanTerms),
    page5: transformToPage5Data(loanTerms, parties),
  };
}

// =============================================================================
// Reverse Transformation Functions (CD Form → Server Data)
// =============================================================================

/**
 * Output structure for reverse transformation
 */
export interface ReverseTransformedData {
  transaction: Partial<Transaction>;
  loanTerms: Partial<LoanTerms> | null;
  parties: Partial<Party>[];
  lineItems: Partial<LineItem>[];
}

/**
 * Parse a date string from CD form format to ISO string
 * CD form uses MM/dd/yyyy format
 */
function parseDateToISO(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  try {
    // Handle MM/dd/yyyy format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    // Try parsing as ISO or other standard formats
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch {
    // Ignore parse errors
  }
  return undefined;
}

/**
 * Parse loan term string to months
 * Example: "30 years" → 360
 */
function parseLoanTermToMonths(termStr: string | undefined): number | undefined {
  if (!termStr) return undefined;
  const match = termStr.match(/(\d+)\s*year/i);
  if (match) {
    return Number(match[1]) * 12;
  }
  return undefined;
}

/**
 * Parse loan type from CD form format to database enum
 */
function parseLoanTypeToEnum(type: string | undefined): LoanType {
  if (!type) return 'conventional';
  const t = type.toLowerCase();
  if (t === 'fha') return 'fha';
  if (t === 'va') return 'va';
  if (t.includes('usda')) return 'usda';
  if (t === 'other') return 'other';
  return 'conventional';
}

/**
 * Parse loan purpose from CD form format to database enum
 */
function parseLoanPurposeToEnum(purpose: string | undefined): LoanPurpose {
  if (!purpose) return 'purchase';
  const p = purpose.toLowerCase();
  if (p.includes('refinance')) return 'refinance';
  if (p.includes('construction')) return 'construction';
  if (p === 'other') return 'other';
  return 'purchase';
}

/**
 * Extract transaction data from CD form
 */
function extractTransactionFromForm(
  formData: ClosingDisclosureData,
  existingTransaction?: Transaction,
): Partial<Transaction> {
  const closingInfo = formData.page1?.header?.closingInformation;

  return {
    // Preserve existing ID and metadata
    id: existingTransaction?.id,
    // Extract from form
    file_number: closingInfo?.fileNumber || existingTransaction?.file_number,
    property_address: closingInfo?.propertyAddress || existingTransaction?.property_address,
    sale_price: closingInfo?.salePrice || existingTransaction?.sale_price,
    closing_date: parseDateToISO(closingInfo?.closingDate) || existingTransaction?.closing_date,
    disbursement_date:
      parseDateToISO(closingInfo?.disbursementDate) || existingTransaction?.disbursement_date,
    settlement_agent_name:
      closingInfo?.settlementAgent || existingTransaction?.settlement_agent_name,
  };
}

/**
 * Extract loan terms from CD form
 */
function extractLoanTermsFromForm(
  formData: ClosingDisclosureData,
  existingLoanTerms?: LoanTerms | null,
  transactionId?: string,
): Partial<LoanTerms> | null {
  const page1 = formData.page1;
  const page5 = formData.page5;

  // If no loan terms data in form, return null
  if (!page1?.loanTerms && !page1?.header?.loanInformation) {
    return null;
  }

  const loanInfo = page1?.header?.loanInformation;
  const loanTerms = page1?.loanTerms;
  const projectedPayments = page1?.projectedPayments;
  const loanCalculations = page5?.loanCalculations;

  // Get the first period for payment info
  const firstPeriod = projectedPayments?.periods?.[0];

  return {
    // Preserve existing ID
    id: existingLoanTerms?.id,
    transaction_id: transactionId || existingLoanTerms?.transaction_id,

    // From loanInformation
    loan_term_months:
      parseLoanTermToMonths(loanInfo?.loanTerm) || existingLoanTerms?.loan_term_months,
    loan_purpose: parseLoanPurposeToEnum(loanInfo?.purpose) || existingLoanTerms?.loan_purpose,
    loan_product: loanInfo?.product || existingLoanTerms?.loan_product,
    loan_type: parseLoanTypeToEnum(loanInfo?.loanType) || existingLoanTerms?.loan_type,
    loan_id: loanInfo?.loanIdNumber || existingLoanTerms?.loan_id,
    mic: loanInfo?.mic || existingLoanTerms?.mic,

    // From loanTerms section
    loan_amount: loanTerms?.loanAmount?.value ?? existingLoanTerms?.loan_amount,
    interest_rate: loanTerms?.interestRate?.value ?? existingLoanTerms?.interest_rate,
    monthly_principal_and_interest:
      loanTerms?.monthlyPrincipalAndInterest?.value ??
      existingLoanTerms?.monthly_principal_and_interest,
    can_increase_after_closing:
      loanTerms?.loanAmount?.canIncrease ?? existingLoanTerms?.can_increase_after_closing,
    prepayment_penalty:
      loanTerms?.prepaymentPenalty?.hasFeature ?? existingLoanTerms?.prepayment_penalty,
    balloon_payment: loanTerms?.balloonPayment?.hasFeature ?? existingLoanTerms?.balloon_payment,

    // From projectedPayments
    estimated_escrow_payment:
      firstPeriod?.estimatedEscrow ?? existingLoanTerms?.estimated_escrow_payment,
    estimated_total_monthly_payment:
      firstPeriod?.estimatedTotalMonthlyPayment ??
      existingLoanTerms?.estimated_total_monthly_payment,
    estimated_taxes_insurance_assessments:
      projectedPayments?.estimatedTaxesInsuranceAssessments ??
      existingLoanTerms?.estimated_taxes_insurance_assessments,
    has_escrow:
      (projectedPayments?.inEscrow && projectedPayments.inEscrow.length > 0) ??
      existingLoanTerms?.has_escrow,
    escrowed_items:
      projectedPayments?.inEscrow?.map((item) => {
        if (item.toLowerCase().includes('tax')) return 'property_taxes' as EscrowedItem;
        if (item.toLowerCase().includes('insurance')) return 'homeowners_insurance' as EscrowedItem;
        return 'other' as EscrowedItem;
      }) ?? existingLoanTerms?.escrowed_items,

    // From loanCalculations (page 5)
    total_of_payments: loanCalculations?.totalOfPayments ?? existingLoanTerms?.total_of_payments,
    finance_charge: loanCalculations?.financeCharge ?? existingLoanTerms?.finance_charge,
    annual_percentage_rate:
      loanCalculations?.annualPercentageRate ?? existingLoanTerms?.annual_percentage_rate,
    total_interest_percentage:
      loanCalculations?.totalInterestPercentage ?? existingLoanTerms?.total_interest_percentage,
  };
}

/**
 * Extract parties from CD form
 */
function extractPartiesFromForm(
  formData: ClosingDisclosureData,
  existingParties: Party[],
  _transactionId?: string,
): Partial<Party>[] {
  const parties: Partial<Party>[] = [];
  const transactionInfo = formData.page1?.header?.transactionInformation;
  const contactInfo = formData.page5?.contactInformation;

  // Helper to find existing party by type
  const findExistingParty = (type: Party['party_type']) =>
    existingParties.find((p) => p.party_type === type);

  // Extract borrowers from transaction info
  if (transactionInfo?.borrowerName) {
    const existingBorrower = findExistingParty('borrower');
    parties.push({
      id: existingBorrower?.id,
      party_type: 'borrower',
      name: transactionInfo.borrowerName,
      // Preserve existing address info
      address_street: existingBorrower?.address_street,
      address_city: existingBorrower?.address_city,
      address_state: existingBorrower?.address_state,
      address_zip_code: existingBorrower?.address_zip_code,
      is_primary: true,
    });
  }

  // Extract sellers from transaction info
  if (transactionInfo?.sellerName) {
    const existingSeller = findExistingParty('seller');
    parties.push({
      id: existingSeller?.id,
      party_type: 'seller',
      name: transactionInfo.sellerName,
      // Preserve existing address info
      address_street: existingSeller?.address_street,
      address_city: existingSeller?.address_city,
      address_state: existingSeller?.address_state,
      address_zip_code: existingSeller?.address_zip_code,
      is_primary: true,
    });
  }

  // Extract lender from contact information (page 5)
  if (contactInfo?.lender) {
    const existingLender = findExistingParty('lender');
    const lender = contactInfo.lender;
    parties.push({
      id: existingLender?.id,
      party_type: 'lender',
      name: lender.name || existingLender?.name || '',
      address_street: lender.address || existingLender?.address_street,
      nmls: lender.nmls || existingLender?.nmls,
      license_id: lender.stateLicense || existingLender?.license_id,
      email: lender.email || existingLender?.email,
      phone: lender.phone || existingLender?.phone,
      is_primary: true,
    });
  }

  // Extract settlement agent from contact information (page 5)
  if (contactInfo?.settlementAgent) {
    const existingAgent = findExistingParty('settlement_agent');
    const agent = contactInfo.settlementAgent;
    parties.push({
      id: existingAgent?.id,
      party_type: 'settlement_agent',
      name: agent.name || existingAgent?.name || '',
      address_street: agent.address || existingAgent?.address_street,
      nmls: agent.nmls || existingAgent?.nmls,
      license_id: agent.stateLicense || existingAgent?.license_id,
      email: agent.email || existingAgent?.email,
      phone: agent.phone || existingAgent?.phone,
      is_primary: true,
    });
  }

  return parties;
}

/**
 * Convert form line item data back to server format
 */
function convertFormLineItemToServer(
  formItem: ClosingCostLineItemData,
  section: LineItem['section'],
  transactionId: string,
  existingItem?: LineItem,
): Partial<LineItem> {
  return {
    id: existingItem?.id,
    transactionId: transactionId,
    section: section,
    lineNumber: (existingItem?.lineNumber ?? Number(formItem.lineNumber)) || 0,
    lineNumberString: String(formItem.lineNumber),
    description: formItem.description,
    borrowerPaidAtClosing: formItem.borrowerPaid?.atClosing ?? 0,
    borrowerPaidBeforeClosing: formItem.borrowerPaid?.beforeClosing ?? 0,
    sellerPaidAtClosing: formItem.sellerPaid?.atClosing ?? 0,
    sellerPaidBeforeClosing: formItem.sellerPaid?.beforeClosing ?? 0,
    paidByOthers: formItem.paidByOthers ?? 0,
    payeeName: existingItem?.payeeName,
    isOptional: existingItem?.isOptional ?? false,
    isUserEntered: existingItem?.isUserEntered ?? true,
  };
}

/**
 * Extract line items from CD form
 *
 * If formData.page2 has no line items in any section, returns existingLineItems
 * (assumes no edits were made). Otherwise extracts items from formData.
 */
function extractLineItemsFromForm(
  formData: ClosingDisclosureData,
  existingLineItems: LineItem[],
  transactionId: string,
): Partial<LineItem>[] {
  const lineItems: Partial<LineItem>[] = [];
  const page2 = formData.page2;

  if (!page2) return existingLineItems;

  // Check if formData has ANY line items in page2
  // If no items exist in any section, return existing items (no edits made)
  const hasAnyFormItems =
    (page2.loanCosts?.originationCharges?.items?.length ?? 0) > 0 ||
    (page2.loanCosts?.servicesYouCannotShopFor?.items?.length ?? 0) > 0 ||
    (page2.loanCosts?.servicesYouCanShopFor?.items?.length ?? 0) > 0 ||
    (page2.otherCosts?.taxesAndGovernmentFees?.items?.length ?? 0) > 0 ||
    (page2.otherCosts?.prepaids?.items?.length ?? 0) > 0 ||
    (page2.otherCosts?.other?.items?.length ?? 0) > 0;

  if (!hasAnyFormItems) return existingLineItems;

  // Helper to find existing line item by section and description
  const findExistingItem = (section: string, description: string) =>
    existingLineItems.find(
      (item) =>
        item.section === section && item.description?.toLowerCase() === description?.toLowerCase(),
    );

  // Section A: Origination Charges
  const sectionAItems = page2.loanCosts?.originationCharges?.items || [];
  for (const item of sectionAItems) {
    const existing = findExistingItem('A', item.description);
    lineItems.push(convertFormLineItemToServer(item, 'A', transactionId, existing));
  }

  // Section B: Services You Cannot Shop For
  const sectionBItems = page2.loanCosts?.servicesYouCannotShopFor?.items || [];
  for (const item of sectionBItems) {
    const existing = findExistingItem('B', item.description);
    lineItems.push(convertFormLineItemToServer(item, 'B', transactionId, existing));
  }

  // Section C: Services You Can Shop For
  const sectionCItems = page2.loanCosts?.servicesYouCanShopFor?.items || [];
  for (const item of sectionCItems) {
    const existing = findExistingItem('C', item.description);
    lineItems.push(convertFormLineItemToServer(item, 'C', transactionId, existing));
  }

  // Section E: Taxes and Government Fees
  const sectionEItems = page2.otherCosts?.taxesAndGovernmentFees?.items || [];
  for (const item of sectionEItems) {
    const existing = findExistingItem('E', item.description);
    lineItems.push(convertFormLineItemToServer(item, 'E', transactionId, existing));
  }

  // Section F/G: Prepaids
  const prepaidItems = page2.otherCosts?.prepaids?.items || [];
  for (const item of prepaidItems) {
    // Try to determine if it's F or G based on existing items
    const existingF = findExistingItem('F', item.description);
    const existingG = findExistingItem('G', item.description);
    const section = existingF ? 'F' : existingG ? 'G' : 'F';
    const existing = existingF || existingG;
    lineItems.push(
      convertFormLineItemToServer(item, section as LineItem['section'], transactionId, existing),
    );
  }

  // Section H: Other
  const sectionHItems = page2.otherCosts?.other?.items || [];
  for (const item of sectionHItems) {
    const existing = findExistingItem('H', item.description);
    lineItems.push(convertFormLineItemToServer(item, 'H', transactionId, existing));
  }

  return lineItems;
}

/**
 * Transform CD form data back to server data format
 *
 * This is the reverse of transformServerDataToCDForm.
 * Used when saving edits made in the CD preview/edit mode.
 */
export function transformCDFormToServerData(
  formData: ClosingDisclosureData,
  existingServerData: ServerData,
): ReverseTransformedData {
  const transactionId = existingServerData.transaction?.id || formData.loanId || '';

  return {
    transaction: extractTransactionFromForm(formData, existingServerData.transaction),
    loanTerms: extractLoanTermsFromForm(formData, existingServerData.loanTerms, transactionId),
    parties: extractPartiesFromForm(formData, existingServerData.parties || [], transactionId),
    lineItems: extractLineItemsFromForm(
      formData,
      existingServerData.lineItems || [],
      transactionId,
    ),
  };
}

/**
 * Helper to remove undefined and null values from an object
 * Creates a clean payload ready for API submission
 * Backend Joi schemas reject null for string/number fields, so we must omit them
 */
function removeNullish<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  }
  return result as Partial<T>;
}

/**
 * Parse a date string from form format (MM/dd/yyyy) to ISO string
 */
function formDateToISO(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined;
  try {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    // Try parsing as ISO or other format
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch {
    // Ignore parse errors
  }
  return undefined;
}

/**
 * Output structure for API-ready payloads
 */
export interface ApiReadyPayloads {
  transaction: Record<string, unknown>;
  loanTerms: Record<string, unknown> | null;
  parties: Record<string, unknown>[];
  lineItems: Record<string, unknown>[];
}

/**
 * Create API-ready payloads by merging form changes into existing server data
 *
 * This is the preferred approach for saving CD form edits:
 * 1. Start with existing server data (has all required fields)
 * 2. Override only the fields that are editable in the form
 * 3. Return clean payloads without undefined values
 *
 * This preserves all server-only fields (prepayment_penalty_details, etc.)
 * that aren't displayed in the CD form.
 */
export function createApiPayloadsFromFormChanges(
  formData: ClosingDisclosureData,
  serverData: ServerData,
): ApiReadyPayloads {
  // ============================================================================
  // Transaction Payload
  // ============================================================================
  const closingInfo = formData.page1?.header?.closingInformation;
  const existingTx = serverData.transaction;

  // Start with existing transaction, override with form values
  const transactionPayload: Record<string, unknown> = {
    // Preserve all existing fields
    property_address: existingTx?.property_address,
    sale_price: existingTx?.sale_price,
    closing_date: existingTx?.closing_date,
    disbursement_date: existingTx?.disbursement_date,
    settlement_agent_name: existingTx?.settlement_agent_name,
    file_number: existingTx?.file_number,
    loan_number: existingTx?.loan_number,
    status: existingTx?.status,
  };

  // Override with form values (only if they exist in the form)
  if (closingInfo) {
    if (closingInfo.propertyAddress) {
      transactionPayload.property_address = closingInfo.propertyAddress;
    }
    if (closingInfo.salePrice !== undefined) {
      transactionPayload.sale_price = closingInfo.salePrice;
    }
    if (closingInfo.closingDate) {
      transactionPayload.closing_date = formDateToISO(closingInfo.closingDate);
    }
    if (closingInfo.disbursementDate) {
      transactionPayload.disbursement_date = formDateToISO(closingInfo.disbursementDate);
    }
    if (closingInfo.settlementAgent) {
      transactionPayload.settlement_agent_name = closingInfo.settlementAgent;
    }
    if (closingInfo.fileNumber) {
      transactionPayload.file_number = closingInfo.fileNumber;
    }
  }

  // ============================================================================
  // Loan Terms Payload
  // ============================================================================
  let loanTermsPayload: Record<string, unknown> | null = null;

  if (serverData.loanTerms) {
    const existingLt = serverData.loanTerms;
    const formLoanTerms = formData.page1?.loanTerms;
    const loanInfo = formData.page1?.header?.loanInformation;
    const projectedPayments = formData.page1?.projectedPayments;
    const loanCalcs = formData.page5?.loanCalculations;
    const firstPeriod = projectedPayments?.periods?.[0];

    // Start with ALL existing loan terms fields (preserves server-only fields)
    loanTermsPayload = {
      // Required fields
      loan_amount: existingLt.loan_amount,
      loan_term_months: existingLt.loan_term_months,
      loan_purpose: existingLt.loan_purpose,
      loan_product: existingLt.loan_product,
      loan_type: existingLt.loan_type,
      interest_rate: existingLt.interest_rate,
      monthly_principal_and_interest: existingLt.monthly_principal_and_interest,
      estimated_total_monthly_payment: existingLt.estimated_total_monthly_payment,
      // Boolean fields
      prepayment_penalty: existingLt.prepayment_penalty ?? false,
      balloon_payment: existingLt.balloon_payment ?? false,
      can_increase_after_closing: existingLt.can_increase_after_closing ?? false,
      has_escrow: existingLt.has_escrow ?? false,
      // Optional fields that exist on server
      loan_id: existingLt.loan_id,
      mic: existingLt.mic,
      escrowed_items: existingLt.escrowed_items,
      estimated_escrow_payment: existingLt.estimated_escrow_payment,
      estimated_taxes_insurance_assessments: existingLt.estimated_taxes_insurance_assessments,
      total_of_payments: existingLt.total_of_payments,
      finance_charge: existingLt.finance_charge,
      annual_percentage_rate: existingLt.annual_percentage_rate,
      total_interest_percentage: existingLt.total_interest_percentage,
    };

    // Preserve conditional required fields from existing data
    const existingAsRecord = existingLt as unknown as Record<string, unknown>;
    if (existingAsRecord.prepayment_penalty_details) {
      loanTermsPayload.prepayment_penalty_details = existingAsRecord.prepayment_penalty_details;
    }
    if (existingAsRecord.balloon_payment_amount !== undefined) {
      loanTermsPayload.balloon_payment_amount = existingAsRecord.balloon_payment_amount;
    }
    if (existingAsRecord.balloon_payment_due_date) {
      loanTermsPayload.balloon_payment_due_date = existingAsRecord.balloon_payment_due_date;
    }
    if (existingAsRecord.first_change_date) {
      loanTermsPayload.first_change_date = existingAsRecord.first_change_date;
    }
    if (existingAsRecord.change_frequency) {
      loanTermsPayload.change_frequency = existingAsRecord.change_frequency;
    }
    if (existingAsRecord.max_rate_increase !== undefined) {
      loanTermsPayload.max_rate_increase = existingAsRecord.max_rate_increase;
    }
    if (existingAsRecord.max_rate_decrease !== undefined) {
      loanTermsPayload.max_rate_decrease = existingAsRecord.max_rate_decrease;
    }
    if (existingAsRecord.rate_adjustment_cap !== undefined) {
      loanTermsPayload.rate_adjustment_cap = existingAsRecord.rate_adjustment_cap;
    }

    // Override with form values where present
    if (formLoanTerms) {
      if (formLoanTerms.loanAmount?.value !== undefined) {
        loanTermsPayload.loan_amount = formLoanTerms.loanAmount.value;
      }
      if (formLoanTerms.interestRate?.value !== undefined) {
        loanTermsPayload.interest_rate = formLoanTerms.interestRate.value;
      }
      if (formLoanTerms.monthlyPrincipalAndInterest?.value !== undefined) {
        loanTermsPayload.monthly_principal_and_interest =
          formLoanTerms.monthlyPrincipalAndInterest.value;
      }
      if (formLoanTerms.loanAmount?.canIncrease !== undefined) {
        loanTermsPayload.can_increase_after_closing = formLoanTerms.loanAmount.canIncrease;
      }
      if (formLoanTerms.prepaymentPenalty?.hasFeature !== undefined) {
        loanTermsPayload.prepayment_penalty = formLoanTerms.prepaymentPenalty.hasFeature;
      }
      if (formLoanTerms.balloonPayment?.hasFeature !== undefined) {
        loanTermsPayload.balloon_payment = formLoanTerms.balloonPayment.hasFeature;
      }
    }

    if (loanInfo) {
      if (loanInfo.loanIdNumber) {
        loanTermsPayload.loan_id = loanInfo.loanIdNumber;
      }
      if (loanInfo.mic) {
        loanTermsPayload.mic = loanInfo.mic;
      }
      if (loanInfo.product) {
        loanTermsPayload.loan_product = loanInfo.product;
      }
    }

    if (firstPeriod) {
      if (firstPeriod.estimatedEscrow !== undefined) {
        loanTermsPayload.estimated_escrow_payment = firstPeriod.estimatedEscrow;
      }
      if (firstPeriod.estimatedTotalMonthlyPayment !== undefined) {
        loanTermsPayload.estimated_total_monthly_payment = firstPeriod.estimatedTotalMonthlyPayment;
      }
    }

    if (projectedPayments?.estimatedTaxesInsuranceAssessments !== undefined) {
      loanTermsPayload.estimated_taxes_insurance_assessments =
        projectedPayments.estimatedTaxesInsuranceAssessments;
    }

    if (loanCalcs) {
      if (loanCalcs.totalOfPayments !== undefined) {
        loanTermsPayload.total_of_payments = loanCalcs.totalOfPayments;
      }
      if (loanCalcs.financeCharge !== undefined) {
        loanTermsPayload.finance_charge = loanCalcs.financeCharge;
      }
      if (loanCalcs.annualPercentageRate !== undefined) {
        loanTermsPayload.annual_percentage_rate = loanCalcs.annualPercentageRate;
      }
      if (loanCalcs.totalInterestPercentage !== undefined) {
        loanTermsPayload.total_interest_percentage = loanCalcs.totalInterestPercentage;
      }
    }

    // Ensure conditional required fields have defaults if their boolean is true
    if (loanTermsPayload.prepayment_penalty && !loanTermsPayload.prepayment_penalty_details) {
      loanTermsPayload.prepayment_penalty_details = 'As specified in loan agreement';
    }
    if (loanTermsPayload.balloon_payment) {
      if (loanTermsPayload.balloon_payment_amount === undefined) {
        loanTermsPayload.balloon_payment_amount = 0;
      }
      if (!loanTermsPayload.balloon_payment_due_date) {
        loanTermsPayload.balloon_payment_due_date = new Date().toISOString();
      }
    }
    if (loanTermsPayload.can_increase_after_closing && !loanTermsPayload.first_change_date) {
      loanTermsPayload.first_change_date = new Date().toISOString();
    }

    // Remove undefined values
    loanTermsPayload = removeNullish(loanTermsPayload) as Record<string, unknown>;
  }

  // ============================================================================
  // Parties Payload (use existing - parties aren't typically edited in CD preview)
  // ============================================================================
  const partiesPayload = (serverData.parties || []).map((party) =>
    removeNullish({ ...party }),
  ) as Record<string, unknown>[];

  // ============================================================================
  // Line Items Payload - Extract from formData to capture edits made in preview
  // ============================================================================
  const transactionId = serverData.transaction?.id || '';
  const extractedLineItems = extractLineItemsFromForm(
    formData,
    serverData.lineItems || [],
    transactionId,
  );
  const lineItemsPayload = extractedLineItems.map((item) => removeNullish({ ...item })) as Record<
    string,
    unknown
  >[];

  return {
    transaction: removeNullish(transactionPayload) as Record<string, unknown>,
    loanTerms: loanTermsPayload,
    parties: partiesPayload,
    lineItems: lineItemsPayload,
  };
}

/**
 * Extract server data from a snapshot for store hydration
 *
 * Returns normalized server data structure from version snapshot
 */
export function extractServerDataFromSnapshot(version: CDVersion): ServerData | null {
  const snapshot = version.snapshotData;

  if (!snapshot) {
    return null;
  }

  const parties = (snapshot.parties || []).map((p) =>
    transformSnapshotParty(p as Record<string, unknown>),
  );
  const lineItems = (snapshot.lineItems || []).map((li) =>
    transformSnapshotLineItem(li as Record<string, unknown>),
  );
  const loanTerms = snapshot.loanTerms
    ? transformSnapshotLoanTerms(snapshot.loanTerms as Record<string, unknown>)
    : null;
  const calculations = transformSnapshotCalculations(
    snapshot.draft?.calculated_totals as Record<string, unknown> | null,
  );

  // Create transaction from snapshot data
  const formData = snapshot.draft?.form_data as Record<string, unknown> | undefined;
  const transaction: Transaction = {
    id: version.transactionId || '',
    property_address: (formData?.property_address as string) || '',
    sale_price: parseNumeric(formData?.sale_price as string | number),
    closing_date: (formData?.closing_date as string) || '',
    file_number: (formData?.file_number as string) || '',
    status: (snapshot.draft?.status as Transaction['status']) || 'draft',
    created_at: version.createdAt || new Date().toISOString(),
    updated_at: version.createdAt || new Date().toISOString(),
  };

  return {
    transaction,
    loanTerms,
    parties,
    lineItems,
    calculations,
  };
}
